'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { WOOSMAP_DEFAULT_MARKER_ICON, WOOSMAP_ID, WOOSMAP_SELECTED_MARKER_ICON } from '@headless-workspace/config';
import { UIStoreInfoCard } from '@headless-workspace/core-ui';
import { Component } from '@headless-workspace/typings';
import { useStoreSelectionHelpers } from '../../../../../client';
import { LocalityDetails, StoreInfoValue } from '../../../../../server';
import { useMap } from './useWoosmapMap';

const MAP_PADDING = { left: 150, right: 150, top: 75, bottom: 75 };
const MAP_ONE_STORE_PADDING = 0.05;

type StoreSelectionMenuMapProps = {
    stores: StoreInfoValue[];
    favoriteStoreId?: string;
    highlightedStoreId?: string;
    setHighlightedStore: (store?: StoreInfoValue) => void;
    currentUserPosition: GeolocationPosition | null;
    localityDetails?: LocalityDetails | null;
};

const getPosition = ({ latitude, longitude }: Pick<GeolocationCoordinates, 'latitude' | 'longitude'>) => ({
    lat: latitude,
    lng: longitude,
});

export const StoreSelectionMenuMap: Component<StoreSelectionMenuMapProps> = ({
    stores,
    favoriteStoreId,
    highlightedStoreId,
    setHighlightedStore,
    currentUserPosition,
    localityDetails,
}) => {
    const { legalMentionsDTSModal, onOpenMenu, mapStoreInfoToUIStoreInfoCardStore } = useStoreSelectionHelpers();

    const selectedMarker = useRef<woosmap.map.Marker | undefined>(undefined);

    const highlightedStore = useMemo(() => {
        return stores.find((store) => store.id === highlightedStoreId);
    }, [highlightedStoreId, stores]);

    const myMap = useMap(document.getElementById(WOOSMAP_ID) as HTMLElement);

    const modalTranslations = useTranslations('Modal.stores.card');
    const commonTranslations = useTranslations('Common.action');

    useEffect(() => {
        if (stores.length === 0 && currentUserPosition) {
            myMap?.setCenter(getPosition(currentUserPosition.coords));
        }
    }, [currentUserPosition, myMap, stores]);

    useEffect(() => {
        if (!myMap || selectedMarker.current) {
            return;
        }

        stores.forEach((store) => {
            const isCurrentStoreHighlighted = store.id === highlightedStoreId;

            const marker = new woosmap.map.Marker({
                position: getPosition(store),
                icon: isCurrentStoreHighlighted ? WOOSMAP_SELECTED_MARKER_ICON : WOOSMAP_DEFAULT_MARKER_ICON,
            });

            marker.setMap(myMap);

            marker.addListener('click', async function () {
                selectedMarker.current?.setIcon(WOOSMAP_DEFAULT_MARKER_ICON);
                marker.setIcon(WOOSMAP_SELECTED_MARKER_ICON);
                selectedMarker.current = marker;
                setHighlightedStore(store);
            });

            if (isCurrentStoreHighlighted && !selectedMarker.current) {
                selectedMarker.current = marker;
            }
        });

        return () => woosmap.map.event.clearInstanceListeners(myMap);
    }, [stores, highlightedStoreId, setHighlightedStore, myMap]);

    // compute bounds of the map
    useEffect(() => {
        if (!myMap) {
            return;
        }
        if (localityDetails && stores.length === 0) {
            myMap.setCenter(getPosition(localityDetails.geometry.location));
            return;
        }

        const bounds = new woosmap.map.LatLngBounds();

        if (localityDetails) {
            const position = getPosition(localityDetails.geometry.location);

            if (stores.length === 1) {
                bounds.extend({ lat: position.lat + MAP_ONE_STORE_PADDING, lng: position.lng + MAP_ONE_STORE_PADDING });
                bounds.extend({ lat: position.lat - MAP_ONE_STORE_PADDING, lng: position.lng - MAP_ONE_STORE_PADDING });
            } else {
                bounds.extend(position);
            }
        }

        stores.forEach((store) => {
            bounds.extend(getPosition(store));
        });

        myMap.fitBounds(bounds, MAP_PADDING);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myMap, localityDetails]);

    const getStoreProps = (store: StoreInfoValue) => ({
        ...mapStoreInfoToUIStoreInfoCardStore(store, favoriteStoreId),
        isSelected: true,
    });

    const handleStoreInfoCardClose = () => {
        if (selectedMarker.current) {
            selectedMarker.current.setIcon(WOOSMAP_DEFAULT_MARKER_ICON);
            selectedMarker.current = undefined;
        }
        setHighlightedStore(undefined);
    };

    return (
        <section className={'relative flex flex-1 self-stretch'}>
            <div id={WOOSMAP_ID} className={'flex flex-1 self-stretch'} />
            <div className={'absolute left-1 right-1 bottom-1 bg-surface-primary'}>
                {highlightedStore && (
                    <UIStoreInfoCard
                        favoriteLabel={modalTranslations('label.favoriteStore')}
                        store={getStoreProps(highlightedStore)}
                        handleDTSInfoClick={() => onOpenMenu(legalMentionsDTSModal)}
                        closeOptions={{
                            label: commonTranslations('label.close'),
                            onClose: handleStoreInfoCardClose,
                        }}
                    />
                )}
            </div>
        </section>
    );
};
